import React from 'react';
import { useAuth } from '@/context/AuthContext';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  User, 
  ShoppingBag, 
  Download, 
  Clock, 
  CheckCircle2, 
  AlertCircle,
  CreditCard,
  Settings,
  Save,
  Building2,
  Phone,
  MapPin,
  Mail,
  LogOut,
  ShieldCheck,
  LayoutDashboard,
  MessageCircle,
  FileText,
  Star,
  Printer,
  Camera,
  Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { db, collection, query, where, onSnapshot, doc, updateDoc, addDoc, getDoc, storage, ref, uploadBytes, getDownloadURL } from '@/firebase';
import { toast } from 'sonner';
import { generateReceipt } from '@/utils/pdfReceipt';
import RequestDocumentForm from './RequestDocumentForm';
import PaymentModal from './PaymentModal';
import ReceiptModal from './ReceiptModal';

interface Order {
  id: string;
  productName?: string;
  fileUrl?: string;
  amount?: number;
  status: string;
  createdAt?: any;
  productId?: string;
  fileUploadedAt?: any;
  rating?: number;
  reviewText?: string;
}

export default function Dashboard() {
  const { user, profile, updateProfile, logout, loading: authLoading } = useAuth();
  
  const isLinkExpired = (order: Order) => {
    if (!order.fileUploadedAt) return false;
    const uploadedTime = order.fileUploadedAt.toDate ? order.fileUploadedAt.toDate() : new Date(order.fileUploadedAt);
    const now = new Date();
    const diffInHours = (now.getTime() - uploadedTime.getTime()) / (1000 * 60 * 60);
    return diffInHours > 24;
  };
  const isSeller = React.useMemo(() => profile?.role === 'admin' || user?.email === 'azmankoe916@gmail.com', [profile, user]);
  const [orders, setOrders] = React.useState<Order[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [isSaving, setIsSaving] = React.useState(false);
  const [showPaymentModal, setShowPaymentModal] = React.useState(false);
  const [selectedOrder, setSelectedOrder] = React.useState<{id: string, productName: string, amount: number} | null>(null);
  const [selectedReceiptOrder, setSelectedReceiptOrder] = React.useState<any | null>(null);
  const [isReceiptModalOpen, setIsReceiptModalOpen] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState(isSeller ? 'overview' : 'orders');
  
  const [showRatingModal, setShowRatingModal] = React.useState(false);
  const [selectedOrderForRating, setSelectedOrderForRating] = React.useState<Order | null>(null);
  const [ratingValue, setRatingValue] = React.useState(5);
  const [reviewText, setReviewText] = React.useState("");
  const [isSubmittingRating, setIsSubmittingRating] = React.useState(false);

  const handleSubmitRating = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOrderForRating || !user) return;
    setIsSubmittingRating(true);
    try {
      const now = new Date();
      await updateDoc(doc(db, 'orders', selectedOrderForRating.id), {
        rating: ratingValue,
        reviewText,
        ratedAt: now
      });

      await addDoc(collection(db, 'productReviews'), {
        orderId: selectedOrderForRating.id,
        productId: selectedOrderForRating.productId,
        productName: selectedOrderForRating.productName,
        userId: user.uid,
        userName: profile?.displayName || user.displayName || 'User',
        userEmail: user.email,
        rating: ratingValue,
        reviewText,
        createdAt: now
      });

      toast.success("Terima kasih atas ulasan Anda!");
      setShowRatingModal(false);
      setSelectedOrderForRating(null);
    } catch (error) {
      toast.error("Gagal mengirim ulasan.");
    } finally {
      setIsSubmittingRating(false);
    }
  };

  // Update activeTab if seller status changes
  React.useEffect(() => {
    setActiveTab(isSeller ? 'overview' : 'orders');
  }, [isSeller]);

  // Profile form state
  const [formData, setFormData] = React.useState({
    displayName: '',
    phoneNumber: '',
    companyName: '',
    address: ''
  });

  const [isUploadingAvatar, setIsUploadingAvatar] = React.useState(false);

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Format file harus berupa gambar.");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Ukuran gambar maksimal 5 MB.");
      return;
    }

    setIsUploadingAvatar(true);
    const toastId = toast.loading("Mengompres dan memperbarui foto profil...");

    const compressImage = (imageFile: File): Promise<string> => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          const img = new window.Image();
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const MAX_WIDTH = 180;
            const MAX_HEIGHT = 180;
            let width = img.width;
            let height = img.height;

            if (width > height) {
              if (width > MAX_WIDTH) {
                height = Math.round(height * (MAX_WIDTH / width));
                width = MAX_WIDTH;
              }
            } else {
              if (height > MAX_HEIGHT) {
                width = Math.round(width * (MAX_HEIGHT / height));
                height = MAX_HEIGHT;
              }
            }

            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
              reject(new Error('Gagal mendapatkan context canvas'));
              return;
            }

            ctx.drawImage(img, 0, 0, width, height);
            const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
            resolve(dataUrl);
          };
          img.onerror = () => reject(new Error('Gagal memproses gambar'));
          img.src = event.target?.result as string;
        };
        reader.onerror = () => reject(new Error('Gagal membaca file'));
        reader.readAsDataURL(imageFile);
      });
    };

    try {
      const compressedBase64 = await compressImage(file);
      await updateProfile({ photoURL: compressedBase64 });
      toast.success("Foto profil berhasil diperbarui!");
    } catch (error) {
      console.error("Gagal menyimpan foto profil:", error);
      toast.error("Gagal mengunggah foto profil. Silakan coba ukuran/format lain.");
    } finally {
      setIsUploadingAvatar(false);
      toast.dismiss(toastId);
    }
  };

  React.useEffect(() => {
    if (profile) {
      setFormData({
        displayName: profile.displayName || '',
        phoneNumber: profile.phoneNumber || '',
        companyName: profile.companyName || '',
        address: profile.address || ''
      });
    }
  }, [profile]);

  React.useEffect(() => {
    if (!user) return;

    const q = query(collection(db, 'orders'), where('userId', '==', user.uid));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const ordersData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Order));
      setOrders(ordersData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await updateProfile(formData);
      toast.success("Profil berhasil diperbarui!");
    } catch (error) {
      toast.error("Gagal memperbarui profil.");
    } finally {
      setIsSaving(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-foreground font-bold animate-pulse">Memuat Dashboard...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background px-4 text-center">
        <AlertCircle className="h-12 w-12 text-foreground/20 mb-4" />
        <h2 className="text-2xl font-bold text-foreground mb-2">Sesi Berakhir</h2>
        <p className="text-foreground/60 mb-6">Silakan masuk kembali untuk mengakses dashboard Anda.</p>
        <Button asChild className="bg-gold text-navy hover:bg-gold-light font-bold px-8 rounded-xl">
          <Link to="/">Kembali ke Beranda</Link>
        </Button>
      </div>
    );
  }

  if (isSeller) {
    return (
      <div className="min-h-screen bg-background pt-32 pb-20 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Admin Sidebar */}
            <div className="lg:col-span-1">
              <Card className="border-none shadow-xl shadow-foreground/5 overflow-hidden sticky top-32">
                <div className="h-24 bg-primary relative">
                  <div className="absolute -bottom-12 left-1/2 -translate-x-1/2">
                    <div className="h-24 w-24 rounded-full border-4 border-card overflow-hidden bg-card shadow-lg">
                      {profile?.photoURL || user?.photoURL ? (
                        <img src={profile?.photoURL || user?.photoURL} alt={user.displayName || ''} className="h-full w-full object-cover" referrerPolicy="no-referrer" />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center bg-gold/10">
                          <User className="h-10 w-10 text-primary" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <CardContent className="pt-16 text-center pb-8">
                  <h3 className="text-xl font-bold text-foreground">{profile?.displayName || user.displayName}</h3>
                  <p className="text-sm text-foreground/60 mb-4">{user.email}</p>
                  <Badge className="bg-gold text-navy font-bold px-4 py-1">ADMIN / PENJUAL</Badge>
                  
                  <div className="mt-8 pt-6 border-t border-border space-y-3">
                    <Button 
                      asChild
                      className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-bold rounded-xl shadow-lg"
                    >
                      <Link to="/admin">
                        <ShieldCheck className="h-4 w-4 mr-2 text-primary" /> Masuk Panel Admin
                      </Link>
                    </Button>
                    <Button 
                      variant="ghost" 
                      onClick={logout}
                      className="w-full text-red-500 hover:text-red-600 hover:bg-red-50 font-bold rounded-xl"
                    >
                      <LogOut className="h-4 w-4 mr-2" /> Keluar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Admin Main Content */}
            <div className="lg:col-span-3">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="bg-card border border-border p-1 rounded-xl mb-8 flex">
                  <TabsTrigger value="overview" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground flex-1">
                    <LayoutDashboard className="h-4 w-4 mr-2" /> Ringkasan Toko
                  </TabsTrigger>
                  <TabsTrigger value="profile" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground flex-1">
                    <Settings className="h-4 w-4 mr-2" /> Pengaturan Profil
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="overview">
                  <div className="space-y-6">
                    <Card className="border-none shadow-xl bg-gradient-to-br from-primary to-primary/80 text-primary-foreground overflow-hidden">
                      <CardContent className="p-8">
                        <div className="flex justify-between items-start">
                          <div>
                            <h2 className="text-3xl font-bold mb-2">Selamat Datang, Admin!</h2>
                            <p className="text-primary-foreground/90 max-w-md">
                              Anda sedang berada di Dashboard Penjual. Di sini Anda dapat memantau status toko dan memperbarui profil profesional Anda.
                            </p>
                          </div>
                          <div className="h-16 w-16 bg-white/20 dark:bg-navy/20 rounded-2xl flex items-center justify-center backdrop-blur-sm shadow-inner">
                            <ShieldCheck className="h-8 w-8 text-primary-foreground" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <Card className="border-none shadow-md hover:shadow-lg transition-shadow">
                        <CardContent className="p-6">
                          <div className="h-10 w-10 bg-blue-500/10 rounded-lg flex items-center justify-center mb-4">
                            <ShoppingBag className="h-5 w-5 text-blue-500" />
                          </div>
                          <h4 className="font-bold text-foreground mb-1">Kelola Pesanan</h4>
                          <p className="text-xs text-foreground/60 mb-4">Lihat dan proses pesanan dari pelanggan Anda.</p>
                          <Button asChild size="sm" variant="outline" className="w-full border-border text-foreground font-bold hover:bg-muted">
                            <Link to="/admin">Buka Pesanan</Link>
                          </Button>
                        </CardContent>
                      </Card>

                      <Card className="border-none shadow-md hover:shadow-lg transition-shadow">
                        <CardContent className="p-6">
                          <div className="h-10 w-10 bg-gold/10 rounded-lg flex items-center justify-center mb-4">
                            <CreditCard className="h-5 w-5 text-primary" />
                          </div>
                          <h4 className="font-bold text-foreground mb-1">Produk Anda</h4>
                          <p className="text-xs text-foreground/60 mb-4">Tambahkan atau perbarui layanan dan produk jasa konstruksi.</p>
                          <Button asChild size="sm" variant="outline" className="w-full border-border text-foreground font-bold hover:bg-muted">
                            <Link to="/admin">Kelola Produk</Link>
                          </Button>
                        </CardContent>
                      </Card>

                      <Card className="border-none shadow-md hover:shadow-lg transition-shadow">
                        <CardContent className="p-6">
                          <div className="h-10 w-10 bg-green-500/10 rounded-lg flex items-center justify-center mb-4">
                            <CheckCircle2 className="h-5 w-5 text-green-500" />
                          </div>
                          <h4 className="font-bold text-foreground mb-1">Status Sistem</h4>
                          <p className="text-xs text-foreground/60 mb-4">Semua sistem berjalan dengan normal dan aman.</p>
                          <div className="flex items-center text-[10px] font-bold text-green-500 uppercase tracking-widest">
                            <span className="h-2 w-2 bg-green-500 rounded-full mr-2 animate-pulse"></span> Online
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="profile">
                  <Card className="border-none shadow-xl shadow-foreground/5">
                    <CardHeader>
                      <CardTitle className="text-xl font-bold text-foreground flex items-center">
                        <Settings className="h-5 w-5 mr-2 text-primary" /> Pengaturan Profil Admin
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <form onSubmit={handleSaveProfile} className="space-y-6">
                        {/* Avatar Upload Section */}
                        <div className="flex flex-col items-center sm:flex-row gap-6 p-4 rounded-2xl bg-muted/20 border border-border/50">
                          <div className="relative group">
                            <div className="h-20 w-20 rounded-full overflow-hidden border-2 border-primary bg-card relative shadow-md">
                              {profile?.photoURL || user?.photoURL ? (
                                <img src={profile?.photoURL || user?.photoURL} alt="Foto Profil" className="h-full w-full object-cover" referrerPolicy="no-referrer" />
                              ) : (
                                <div className="h-full w-full flex items-center justify-center bg-primary/10">
                                  <User className="h-8 w-8 text-primary" />
                                </div>
                              )}
                              {isUploadingAvatar && (
                                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                  <Loader2 className="h-6 w-6 text-white animate-spin" />
                                </div>
                              )}
                            </div>
                            <label className="absolute bottom-0 right-0 p-1.5 bg-primary rounded-full border border-card shadow-md cursor-pointer hover:scale-110 transition-all">
                              <Camera className="h-3.5 w-3.5 text-primary-foreground" />
                              <input 
                                type="file" 
                                accept="image/*" 
                                className="hidden" 
                                onChange={handleAvatarUpload} 
                                disabled={isUploadingAvatar}
                              />
                            </label>
                          </div>
                          <div className="text-center sm:text-left space-y-1">
                            <p className="font-bold text-foreground text-sm">Foto Profil Admin</p>
                            <p className="text-xs text-foreground/50">Unggah foto profil dalam format JPG, PNG atau WEBP (Maksimal 2MB).</p>
                            <label className="mt-2 inline-flex items-center text-xs text-primary font-bold hover:underline cursor-pointer">
                              Ubah Foto Profil
                              <input 
                                type="file" 
                                accept="image/*" 
                                className="hidden" 
                                onChange={handleAvatarUpload} 
                                disabled={isUploadingAvatar}
                              />
                            </label>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <Label htmlFor="displayName" className="text-foreground font-semibold">Nama Admin</Label>
                            <div className="relative">
                              <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/40" />
                              <Input 
                                id="displayName"
                                value={formData.displayName}
                                onChange={(e) => setFormData({...formData, displayName: e.target.value})}
                                className="pl-10 border-foreground/10 focus:border-primary bg-card text-foreground"
                                placeholder="Nama Admin"
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="email" className="text-foreground font-semibold">Email Bisnis</Label>
                            <div className="relative">
                              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/40" />
                              <Input 
                                id="email"
                                value={user.email || ''}
                                disabled
                                className="pl-10 bg-muted border-foreground/10 cursor-not-allowed text-foreground/60"
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="phoneNumber" className="text-foreground font-semibold">Nomor WhatsApp Bisnis</Label>
                            <div className="relative">
                              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/40" />
                              <Input 
                                id="phoneNumber"
                                value={formData.phoneNumber}
                                onChange={(e) => setFormData({...formData, phoneNumber: e.target.value})}
                                className="pl-10 border-foreground/10 focus:border-primary bg-card text-foreground"
                                placeholder="+62..."
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="companyName" className="text-foreground font-semibold">Nama Perusahaan/Brand</Label>
                            <div className="relative">
                              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/40" />
                              <Input 
                                id="companyName"
                                value={formData.companyName}
                                onChange={(e) => setFormData({...formData, companyName: e.target.value})}
                                className="pl-10 border-foreground/10 focus:border-primary bg-card text-foreground"
                                placeholder="Azman204"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="flex justify-end">
                          <Button 
                            type="submit" 
                            disabled={isSaving}
                            className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold px-8 rounded-xl"
                          >
                            {isSaving ? "Menyimpan..." : (
                              <>
                                <Save className="h-4 w-4 mr-2" /> Simpan Perubahan
                              </>
                            )}
                          </Button>
                        </div>
                      </form>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed': return <Badge className="bg-green-500"><CheckCircle2 className="h-3 w-3 mr-1" /> Selesai</Badge>;
      case 'processing': return <Badge className="bg-blue-500"><Clock className="h-3 w-3 mr-1" /> Diproses</Badge>;
      case 'pending': return <Badge className="bg-yellow-500"><AlertCircle className="h-3 w-3 mr-1" /> Menunggu Pembayaran</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="min-h-screen bg-background pt-32 pb-20 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Profile Sidebar */}
          <div className="lg:col-span-1">
              <Card className="border-none shadow-xl shadow-foreground/5 overflow-hidden sticky top-32">
              <div className="h-24 bg-primary relative">
                <div className="absolute -bottom-12 left-1/2 -translate-x-1/2">
                  <div className="h-24 w-24 rounded-full border-4 border-card overflow-hidden bg-card shadow-lg">
                    {profile?.photoURL || user?.photoURL ? (
                      <img src={profile?.photoURL || user?.photoURL} alt={user.displayName || ''} className="h-full w-full object-cover" referrerPolicy="no-referrer" />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center bg-gold/10">
                        <User className="h-10 w-10 text-primary" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <CardContent className="pt-16 text-center pb-8">
                <h3 className="text-xl font-bold text-foreground">{profile?.displayName || user.displayName}</h3>
                <p className="text-sm text-foreground/60 mb-4">{user.email}</p>
                <div className="flex flex-wrap justify-center gap-2">
                  <Badge variant="outline" className="border-primary text-primary capitalize">
                    {isSeller ? 'Penjual (Admin)' : 'Pembeli'}
                  </Badge>
                  {profile?.companyName && (
                    <Badge variant="secondary" className="bg-muted text-foreground">
                      <Building2 className="h-3 w-3 mr-1" /> {profile.companyName}
                    </Badge>
                  )}
                </div>
                <div className="mt-8 pt-6 border-t border-border space-y-3">
                  {isSeller && (
                    <Button 
                      asChild
                      className="w-full bg-gold text-navy hover:bg-gold-light font-bold rounded-xl shadow-lg shadow-gold/20"
                    >
                      <Link to="/admin">
                        <ShieldCheck className="h-4 w-4 mr-2" /> Kelola Toko
                      </Link>
                    </Button>
                  )}
                  <Button 
                    variant="ghost" 
                    onClick={logout}
                    className="w-full text-red-500 hover:text-red-600 hover:bg-red-500/10 font-bold rounded-xl"
                  >
                    <LogOut className="h-4 w-4 mr-2" /> Keluar dari Akun
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <div className="relative mb-6">
                <TabsList className="w-full bg-card border border-border p-1 rounded-xl flex overflow-x-auto overflow-y-hidden whitespace-nowrap justify-start sm:justify-center scrollbar-hide">
                  {isSeller ? (
                    <TabsTrigger value="overview" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground flex-1 min-w-[120px] sm:min-w-0 text-xs sm:text-sm py-2">
                      <LayoutDashboard className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" /> 
                      <span className="inline">Ringkasan</span>
                    </TabsTrigger>
                  ) : (
                    <TabsTrigger value="orders" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground flex-1 min-w-[110px] sm:min-w-0 text-xs sm:text-sm py-2">
                      <ShoppingBag className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" /> 
                      <span>Pesanan</span>
                    </TabsTrigger>
                  )}
                  {!isSeller && (
                    <TabsTrigger value="downloads" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground flex-1 min-w-[110px] sm:min-w-0 text-xs sm:text-sm py-2">
                      <Download className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" /> 
                      <span>File Akun</span>
                    </TabsTrigger>
                  )}
                  {!isSeller && (
                    <TabsTrigger value="requests" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground flex-1 min-w-[110px] sm:min-w-0 text-xs sm:text-sm py-2">
                      <FileText className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" /> 
                      <span>Request</span>
                    </TabsTrigger>
                  )}
                  <TabsTrigger value="profile" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground flex-1 min-w-[110px] sm:min-w-0 text-xs sm:text-sm py-2">
                    <Settings className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" /> 
                    <span>Profil</span>
                  </TabsTrigger>
                </TabsList>
              </div>

              {isSeller && (
                <TabsContent value="overview">
                  <div className="space-y-6">
                    <Card className="border-none shadow-xl bg-primary text-primary-foreground overflow-hidden">
                      <CardContent className="p-8">
                        <div className="flex justify-between items-center">
                          <div>
                            <h2 className="text-2xl font-bold mb-2">Halo, Admin Azman!</h2>
                            <p className="text-primary-foreground/90">Selamat datang di dashboard penjual Anda.</p>
                          </div>
                          <ShieldCheck className="h-12 w-12 text-primary-foreground opacity-30" />
                        </div>
                      </CardContent>
                    </Card>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Card className="border-none shadow-md">
                        <CardHeader>
                          <CardTitle className="text-sm font-bold text-foreground/60 uppercase tracking-wider">Status Toko</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="flex items-center gap-4">
                            <div className="h-12 w-12 bg-green-50 rounded-full flex items-center justify-center">
                              <CheckCircle2 className="h-6 w-6 text-green-600" />
                            </div>
                            <div>
                              <p className="text-lg font-bold text-foreground">Aktif</p>
                              <p className="text-xs text-foreground/40">Toko Anda sedang menerima pesanan</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                      <Card className="border-none shadow-md">
                        <CardHeader>
                          <CardTitle className="text-sm font-bold text-foreground/60 uppercase tracking-wider">Aksi Cepat</CardTitle>
                        </CardHeader>
                        <CardContent className="flex gap-2">
                          <Button asChild size="sm" className="bg-gold text-navy hover:bg-gold-light font-bold flex-1">
                            <Link to="/admin">Lihat Pesanan</Link>
                          </Button>
                          <Button asChild variant="outline" size="sm" className="border-foreground/10 text-foreground flex-1">
                            <Link to="/admin">Tambah Produk</Link>
                          </Button>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </TabsContent>
              )}

              <TabsContent value="orders">
                <div className="space-y-4">
                  {loading ? (
                    <div className="text-center py-12 text-foreground/40">Memuat data...</div>
                  ) : orders.length === 0 ? (
                    <Card className="border-dashed border-2 border-foreground/10 bg-transparent">
                      <CardContent className="flex flex-col items-center py-12">
                        <ShoppingBag className="h-12 w-12 text-foreground/20 mb-4" />
                        <p className="text-foreground/60 font-medium">Belum ada pesanan.</p>
                        {!isSeller && (
                          <Button asChild variant="link" className="text-primary">
                            <a href="/products">Mulai Belanja</a>
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  ) : (
                    orders.map((order) => (
                      <Card key={order.id} className="border-none shadow-md hover:shadow-lg transition-shadow overflow-hidden">
                        <CardContent className="p-4 sm:p-6 bg-card">
                          <div className="flex flex-col gap-6 text-foreground">
                            {/* Card Top: Product Info */}
                            <div className="flex items-start gap-4">
                              <div className="h-12 w-12 bg-muted rounded-xl flex items-center justify-center shrink-0">
                                <CreditCard className="h-6 w-6 text-foreground" />
                              </div>
                              <div className="flex-1 min-w-0 text-foreground">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 mb-1">
                                  <h4 className="font-bold text-foreground pr-2">{order.productName}</h4>
                                  <div className="flex items-center gap-2">
                                    <Button 
                                      variant="ghost" 
                                      size="icon" 
                                      className="h-6 w-6 text-foreground/40 hover:text-primary transition-colors"
                                      onClick={() => {
                                        setSelectedReceiptOrder(order);
                                        setIsReceiptModalOpen(true);
                                      }}
                                      title={order.status === 'completed' ? "Lihat & Cetak Kuitansi Resmi (Lunas)" : "Cetak Invoice Tagihan (PDF)"}
                                    >
                                      <Printer className="h-3.5 w-3.5" />
                                    </Button>
                                    <span className="text-[10px] sm:text-xs text-foreground/40 font-medium whitespace-nowrap">
                                      {order.createdAt?.toDate ? order.createdAt.toDate().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}
                                    </span>
                                  </div>
                                </div>
                                <p className="text-[10px] sm:text-xs text-foreground/40 mb-3">ID: {order.id}</p>
                                
                                <div className="flex flex-wrap items-center gap-3">
                                  <span className="text-lg font-bold text-primary">
                                    Rp {order.amount?.toLocaleString('id-ID')}
                                  </span>
                                  {getStatusBadge(order.status)}
                                </div>
                              </div>
                            </div>

                            {/* Card Bottom: Actions */}
                            <div className="pt-4 border-t border-border">
                              {order.status === 'pending' && (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                  <Button 
                                    className="bg-gold text-navy hover:bg-gold-light font-bold h-10 rounded-xl order-1 sm:order-2"
                                    onClick={() => {
                                      setSelectedOrder({
                                        id: order.id,
                                        productName: order.productName || 'Produk',
                                        amount: order.amount || 0
                                      });
                                      setShowPaymentModal(true);
                                    }}
                                  >
                                    Konfirmasi Pembayaran
                                  </Button>
                                  <Button 
                                    variant="outline"
                                    className="border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white font-bold h-10 rounded-xl order-2 sm:order-1"
                                    asChild
                                  >
                                    <a 
                                      href={`https://wa.me/6281261089400?text=${encodeURIComponent(
                                        `Halo Admin Azman, saya ingin mengirim bukti pembayaran untuk pesanan saya.\n\n` +
                                        `📌 *ID Pesanan:* ${order.id}\n` +
                                        `📦 *Produk:* ${order.productName}\n` +
                                        `💰 *Nominal:* Rp ${order.amount?.toLocaleString('id-ID')}`
                                      )}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      <MessageCircle className="h-4 w-4 mr-2" /> Kirim Bukti ke WA
                                    </a>
                                  </Button>
                                </div>
                              )}
                              {order.status === 'completed' && (
                                <div className="space-y-3">
                                  <Button 
                                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-bold h-10 rounded-xl"
                                    onClick={() => {
                                      if (isLinkExpired(order)) {
                                        toast.error("Link download sudah kedaluwarsa (lebih dari 24 jam).");
                                      } else if (order.fileUrl) {
                                        window.open(order.fileUrl, '_blank');
                                      } else {
                                        toast.error("File produk belum tersedia.");
                                      }
                                    }}
                                  >
                                    <Download className="h-4 w-4 mr-2 text-primary" /> Download File Produk
                                  </Button>
                                  
                                  {!order.rating && (
                                    <Button
                                      variant="outline"
                                      className="w-full border-gold text-gold hover:bg-gold hover:text-navy font-bold h-10 rounded-xl"
                                      onClick={() => {
                                        setSelectedOrderForRating(order);
                                        setRatingValue(5);
                                        setReviewText("");
                                        setShowRatingModal(true);
                                      }}
                                    >
                                      <Star className="h-4 w-4 mr-2" /> Berikan Ulasan
                                    </Button>
                                  )}
                                  {order.rating && (
                                    <div className="flex items-center justify-center gap-1 text-gold text-xs font-bold pt-2">
                                      <Star className="h-4 w-4 fill-current" />
                                      Anda memberikan bintang {order.rating}
                                    </div>
                                  )}
                                </div>
                              )}
                              {order.status === 'processing' && (
                                <p className="text-xs text-center text-foreground/40 italic">Pembayaran sedang diverifikasi oleh admin. Mohon ditunggu.</p>
                              )}
                              {order.status === 'awaiting_confirmation' && (
                                <p className="text-xs text-center text-foreground/40 italic">Menunggu konfirmasi manual dari admin.</p>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </TabsContent>

              <TabsContent value="downloads">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {orders.filter(o => o.status === 'completed').length === 0 ? (
                    <div className="col-span-full text-center py-12 text-foreground/40">
                      Belum ada file yang siap di-download.
                    </div>
                  ) : (
                    orders.filter(o => o.status === 'completed').map((order) => (
                      <Card key={order.id} className="border-none shadow-md bg-card">
                        <CardContent className="p-4 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 bg-green-500/10 rounded-lg flex items-center justify-center">
                              <Download className="h-5 w-5 text-green-500" />
                            </div>
                            <div>
                              <h5 className="font-bold text-sm text-foreground">{order.productName}</h5>
                              <p className="text-xs text-foreground/40">Siap di-download</p>
                            </div>
                          </div>
                          <Button 
                            size="icon" 
                            variant="ghost" 
                            className="text-foreground hover:text-primary hover:bg-muted"
                            onClick={() => {
                              if (isLinkExpired(order)) {
                                toast.error("Link download sudah kedaluwarsa.");
                              } else if (order.fileUrl) {
                                window.open(order.fileUrl, '_blank');
                              } else {
                                toast.error("File produk belum tersedia.");
                              }
                            }}
                          >
                            <Download className="h-5 w-5" />
                          </Button>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </TabsContent>

              <TabsContent value="requests">
                <Card className="border-none shadow-xl shadow-foreground/5 bg-card">
                  <CardHeader>
                    <CardTitle className="text-xl font-bold text-foreground flex items-center">
                      <FileText className="h-5 w-5 mr-2 text-primary" /> Request Dokumen Khusus
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <RequestDocumentForm onSuccess={() => {}} />
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="profile">
                <Card className="border-none shadow-xl shadow-foreground/5 bg-card">
                  <CardHeader>
                    <CardTitle className="text-xl font-bold text-foreground flex items-center">
                      <Settings className="h-5 w-5 mr-2 text-primary" /> Informasi Pribadi
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSaveProfile} className="space-y-6">
                      {/* Avatar Upload Section */}
                      <div className="flex flex-col items-center sm:flex-row gap-6 p-4 rounded-2xl bg-muted/20 border border-border/50">
                        <div className="relative group">
                          <div className="h-20 w-20 rounded-full overflow-hidden border-2 border-primary bg-card relative shadow-md">
                            {profile?.photoURL || user?.photoURL ? (
                              <img src={profile?.photoURL || user?.photoURL} alt="Foto Profil" className="h-full w-full object-cover" referrerPolicy="no-referrer" />
                            ) : (
                              <div className="h-full w-full flex items-center justify-center bg-primary/10">
                                <User className="h-8 w-8 text-primary" />
                              </div>
                            )}
                            {isUploadingAvatar && (
                              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                <Loader2 className="h-6 w-6 text-white animate-spin" />
                              </div>
                            )}
                          </div>
                          <label className="absolute bottom-0 right-0 p-1.5 bg-primary rounded-full border border-card shadow-md cursor-pointer hover:scale-110 transition-all">
                            <Camera className="h-3.5 w-3.5 text-primary-foreground" />
                            <input 
                              type="file" 
                              accept="image/*" 
                              className="hidden" 
                              onChange={handleAvatarUpload} 
                              disabled={isUploadingAvatar}
                            />
                          </label>
                        </div>
                        <div className="text-center sm:text-left space-y-1">
                          <p className="font-bold text-foreground text-sm">Foto Profil Pengguna</p>
                          <p className="text-xs text-foreground/50">Unggah foto profil dalam format JPG, PNG atau WEBP (Maksimal 2MB).</p>
                          <label className="mt-2 inline-flex items-center text-xs text-primary font-bold hover:underline cursor-pointer">
                            Ubah Foto Profil
                            <input 
                              type="file" 
                              accept="image/*" 
                              className="hidden" 
                              onChange={handleAvatarUpload} 
                              disabled={isUploadingAvatar}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="displayName" className="text-foreground font-semibold">Nama Lengkap</Label>
                          <div className="relative">
                            <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/40" />
                            <Input 
                              id="displayName"
                              value={formData.displayName}
                              onChange={(e) => setFormData({...formData, displayName: e.target.value})}
                              className="pl-10 border-foreground/10 focus:border-primary bg-card text-foreground"
                              placeholder="Nama Lengkap"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email" className="text-foreground font-semibold">Email</Label>
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/40" />
                            <Input 
                              id="email"
                              value={user.email || ''}
                              disabled
                              className="pl-10 bg-muted border-foreground/10 cursor-not-allowed text-foreground/60"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="phoneNumber" className="text-foreground font-semibold">Nomor WhatsApp</Label>
                          <div className="relative">
                            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/40" />
                            <Input 
                              id="phoneNumber"
                              value={formData.phoneNumber}
                              onChange={(e) => setFormData({...formData, phoneNumber: e.target.value})}
                              className="pl-10 border-foreground/10 focus:border-primary bg-card text-foreground"
                              placeholder="+62..."
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="companyName" className="text-foreground font-semibold">Nama Perusahaan</Label>
                          <div className="relative">
                            <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/40" />
                            <Input 
                              id="companyName"
                              value={formData.companyName}
                              onChange={(e) => setFormData({...formData, companyName: e.target.value})}
                              className="pl-10 border-foreground/10 focus:border-primary bg-card text-foreground"
                              placeholder="PT. Contoh Jaya"
                            />
                          </div>
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <Label htmlFor="address" className="text-foreground font-semibold">Alamat</Label>
                          <div className="relative">
                            <MapPin className="absolute left-3 top-3 h-4 w-4 text-foreground/40" />
                            <textarea 
                              id="address"
                              value={formData.address}
                              onChange={(e) => setFormData({...formData, address: e.target.value})}
                              className="w-full min-h-[100px] pl-10 pr-4 py-2 rounded-lg border border-foreground/10 focus:border-primary outline-none text-sm bg-card text-foreground"
                              placeholder="Alamat lengkap..."
                            />
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <Button 
                          type="submit" 
                          disabled={isSaving}
                          className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold px-8 rounded-xl"
                        >
                          {isSaving ? "Menyimpan..." : (
                            <>
                              <Save className="h-4 w-4 mr-2" /> Simpan Perubahan
                            </>
                          )}
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      <PaymentModal 
        isOpen={showPaymentModal} 
        onClose={() => setShowPaymentModal(false)} 
        order={selectedOrder} 
      />

      <Dialog open={showRatingModal} onOpenChange={setShowRatingModal}>
        <DialogContent className="sm:max-w-[425px] bg-card rounded-2xl border-border">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Berikan Ulasan Anda</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-foreground/70 mb-4">
              Bagaimana pengalaman Anda menggunakan {selectedOrderForRating?.productName || "produk ini"}?
            </p>
            <div className="flex items-center justify-center gap-2 mb-6 cursor-pointer">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  onClick={() => setRatingValue(star)}
                  className={`h-8 w-8 transition-colors ${ratingValue >= star ? "text-gold fill-current" : "text-foreground/20"}`}
                />
              ))}
            </div>
            <div className="space-y-2">
              <Label htmlFor="review" className="text-foreground font-semibold">Tulis ulasan (opsional)</Label>
              <textarea
                id="review"
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
                className="w-full min-h-[100px] p-3 rounded-xl border border-foreground/10 focus:border-primary outline-none text-sm bg-background text-foreground"
                placeholder="Bagikan pendapat Anda tentang produk ini..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowRatingModal(false)}
              className="rounded-xl border-foreground/10 text-foreground"
            >
              Nanti Saja
            </Button>
            <Button
              onClick={handleSubmitRating}
              disabled={isSubmittingRating}
              className="bg-gold text-navy hover:bg-gold-light rounded-xl font-bold"
            >
              {isSubmittingRating ? "Menyimpan..." : "Kirim Ulasan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Interactive Official Receipt & Invoice Modal */}
      <ReceiptModal
        isOpen={isReceiptModalOpen}
        onClose={() => setIsReceiptModalOpen(false)}
        order={selectedReceiptOrder}
        userName={profile?.displayName || user?.displayName || ''}
        userEmail={user?.email || ''}
      />
    </div>
  );
}
